import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../useAuth';
import { useAnalytics } from '../../analytics';
import { isValidReturnUrl } from '../utils/returnUrlValidator';

interface CallbackState {
  readonly status: 'processing' | 'error';
  readonly error: Error | null;
}

export function AuthCallback() {
  const navigate = useNavigate();
  const { handleSigninCallback, login } = useAuth();
  const { track } = useAnalytics();
  const [state, setState] = useState<CallbackState>({
    status: 'processing',
    error: null,
  });
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (processedRef.current) return;
    processedRef.current = true;

    const processCallback = async () => {
      try {
        const user = await handleSigninCallback();
        track('Pocket Admin Login Completed');
        const state = user?.state as { returnUrl?: string } | undefined;
        const returnUrl = state?.returnUrl;
        const destination = isValidReturnUrl(returnUrl) ? returnUrl : '/';
        navigate(destination, { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        setState({
          status: 'error',
          error: error instanceof Error ? error : new Error('Authentication failed'),
        });
      }
    };

    processCallback();
  }, [handleSigninCallback, navigate, track]);

  const handleRetry = () => {
    login();
  };

  if (state.status === 'error') {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: '16px',
      }}>
        <h2>Authentication Error</h2>
        <p>{state.error?.message || 'An error occurred during authentication'}</p>
        <button onClick={handleRetry} style={{ padding: '8px 16px', cursor: 'pointer' }}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh' 
    }}>
      <span>Completing sign in...</span>
    </div>
  );
}
